import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import css from './FeedbackForSection.css';
import classNames from 'classnames';
import { IconThumbsUpL } from '../../icons';
import { Form as FinalForm } from 'react-final-form';
import { Form, FieldTextInput, Button, IconSpinner, Alert } from '../../components';
import { reportFeedbackOnSection } from '../../util/api';
import { useRequest } from '../../hooks/useRequest';
/**
 *
 * @param {Object} props
 * @param {string} props.sectionId
 * @param {string} [props.analysisId]
 * @param {string} [props.heading]
 * @param {string} [props.rootClassName]
 * @param {object} [props.extraParams]
 * @returns
 */
const FeedbackForSection = ({
    heading,
    analysisId,
    sectionId,
    rootClassName,
    extraParams = {},
}) => {
    const currentUser = useSelector(s => s.user.currentUser);
    const [currentFeedback, setCurrentFeedback] = useState(null); // positive | negative
    const [feedbackInputOpen, setFeedbackInputOpen] = useState(false);

    const { loading, error, request } = useRequest();

    if (!currentUser || !currentUser?.id?.uuid) return null;

    const { userType } = currentUser?.attributes?.profile?.publicData || {};
    const { feedbackHistory } = currentUser?.attributes?.profile?.privateData || {};
    const feedbackHistoryByUserType = feedbackHistory ? feedbackHistory[userType] : null;

    const checkIfFeedbackAlreadyExists = feedbackEntity =>
        feedbackEntity.analysisId
            ? feedbackEntity.sectionId === sectionId && feedbackEntity.analysisId === analysisId
            : feedbackEntity.sectionId === sectionId;

    const existingFeedback =
        Array.isArray(feedbackHistoryByUserType) &&
        feedbackHistoryByUserType.find(checkIfFeedbackAlreadyExists);

    /**
     * @param {'positive' | 'negative'} feedbackStatus
     * @param {string} [negativeFeedbackText]
     */
    const sendFeedbackPerDialog = async (feedbackStatus, negativeFeedbackText) => {
        try {
            const isNegative = feedbackStatus === 'negative';

            const body = {
                analysisId,
                sectionId,
                rating: isNegative ? -1 : 1,
                feedback: isNegative ? negativeFeedbackText : null,
                ...extraParams,
            };

            await request(async () => await reportFeedbackOnSection(body));
        } catch (e) {}
    };

    const handleFeedbackPerDialog = feedbackStatus => {
        if (existingFeedback) return;

        setFeedbackInputOpen(false);

        if (feedbackStatus === currentFeedback) {
            return setCurrentFeedback(null);
        }

        setCurrentFeedback(feedbackStatus);

        const isPositive = feedbackStatus === 'positive';

        if (isPositive) {
            return sendFeedbackPerDialog(feedbackStatus, null);
        }

        setFeedbackInputOpen(true);
    };

    if (loading) {
        return <IconSpinner />;
    }

    if (error) {
        return (
            <section className={css.secondRoot}>
                <Alert
                    showAsPlainText
                    header="Es ist ein Fehler aufgetreten"
                    description={
                        error || 'Etwas ist schiefgelaufen, versuchen Sie es später noch einmal.'
                    }
                    type="failure"
                />
            </section>
        );
    }

    return (
        <>
            <section className={classNames(css.root, rootClassName)}>
                <h4 className={css.heading}>{heading || 'War diese Übersicht hilfreich?'}</h4>
                <div className={css.infoHolder}>
                    <p className={css.description}>
                        Dein Feedback hilft uns, das Matching zu verbessern.
                    </p>
                    <aside className={css.feedbackActionsHolder}>
                        <button
                            onClick={() => handleFeedbackPerDialog('positive')}
                            className={classNames(css.feedbackActionButton, {
                                [css.alreadySent]: existingFeedback,
                                [css.selected]:
                                    currentFeedback === 'positive' ||
                                    (existingFeedback && existingFeedback?.rating === 1),
                            })}
                        >
                            <IconThumbsUpL />
                        </button>
                        <button
                            onClick={() => handleFeedbackPerDialog('negative')}
                            className={classNames(css.feedbackActionButton, css.negative, {
                                [css.alreadySent]: existingFeedback,
                                [css.selected]:
                                    currentFeedback === 'negative' ||
                                    (existingFeedback && existingFeedback?.rating === -1),
                            })}
                        >
                            <IconThumbsUpL />
                        </button>
                    </aside>
                </div>
            </section>
            {feedbackInputOpen && (
                <aside className={classNames(css.root, css.secondRoot)}>
                    <FinalForm
                        onSubmit={({ currentFeedback }) => {
                            sendFeedbackPerDialog('negative', currentFeedback);
                            setFeedbackInputOpen(false);
                        }}
                        initialValues={{}}
                        render={fieldRenderProps => {
                            const { handleSubmit, values } = fieldRenderProps;
                            const { currentFeedback } = values || {};

                            const disabled = !currentFeedback;

                            return (
                                <Form onSubmit={handleSubmit}>
                                    <div className={css.formField}>
                                        <FieldTextInput
                                            type="textarea"
                                            id="currentFeedback"
                                            name="currentFeedback"
                                            placeholder="Dein Feedback..."
                                            maxLength={1000}
                                        />
                                    </div>
                                    <div>
                                        <Button disabled={disabled} type="submit">
                                            Feedback senden
                                        </Button>
                                    </div>
                                </Form>
                            );
                        }}
                    />
                </aside>
            )}
            {!feedbackInputOpen && (existingFeedback || currentFeedback) && (
                <aside className={classNames(css.root, css.secondRoot)}>
                    <p className={css.feedbackSentNotification}>Vielen Dank für dein Feedback!</p>
                </aside>
            )}
        </>
    );
};

export default FeedbackForSection;
